import { useState } from 'react';
import { 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt, 
  FaClock,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle
} from 'react-icons/fa';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useAuth } from '../../../hooks/useAuth';
import { useServiceList } from '../../../hooks/useServiceList';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';

const RECAPTCHA_SITE_KEY = '6Le9fJEsAAAAAKGDUgws6tJNgO_6cZ7-74CEVq3o';

// ─────────────────────────────────────────────────────────────────────────────
// Inner form — must live inside GoogleReCaptchaProvider to use the hook
// ─────────────────────────────────────────────────────────────────────────────
const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    address: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState('');

  const { currentUser } = useAuth();
  const { loading: servicesLoading, getServiceOptions } = useServiceList();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const serviceOptions = getServiceOptions();

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', service: '', address: '', message: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      // Basic validation
      if (!formData.name || !formData.email || !formData.message) {
        throw new Error('Please fill in all required fields');
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please enter a valid email address');
      }

      if (!formData.phone || formData.phone.trim() === '') {
        throw new Error('Phone number is required');
      }

      if (!formData.address || formData.address.trim() === '') {
        throw new Error('Property address is required');
      }

      // Get reCAPTCHA v3 token — invisible to the user, runs in background
      if (!executeRecaptcha) {
        throw new Error('reCAPTCHA not ready yet. Please try again in a moment.');
      }
      const recaptchaToken = await executeRecaptcha('quote_form');

      const selectedService = serviceOptions.find(s => s.value === formData.service);
      const serviceName = selectedService?.label ?? (formData.service || 'General Inquiry');

      // Save to Firestore
      const quoteData = {
        customerName: formData.name,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        propertyAddress: formData.address.trim(),
        service: {
          name: serviceName,
          category: formData.service || 'general',
          description: formData.message
        },
        serviceRequested: formData.service || 'general-inquiry',
        message: formData.message,
        source: 'website_form',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        userId: currentUser?.uid || null,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
        // Note: recaptchaToken intentionally NOT stored in Firestore —
        // it's single-use and only needed for the Cloud Function verification
      };

      const docRef = await addDoc(collection(db, 'quotes'), quoteData);
      console.log('Quote submitted:', docRef.id);

      // Send email notification — Cloud Function verifies the reCAPTCHA token
      try {
        const response = await fetch(
          'https://us-central1-winnepocket-landscaping.cloudfunctions.net/sendQuoteRequestEmail',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              address: formData.address.trim(),
              service: serviceName,
              message: formData.message,
              quoteId: docRef.id,
              recaptchaToken // verified server-side in the Cloud Function
            })
          }
        );

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          // If reCAPTCHA specifically failed, surface it to the user
          if (response.status === 400 && errData.error?.includes('reCAPTCHA')) {
            throw new Error('Bot verification failed. Please refresh the page and try again.');
          }
          // Otherwise email failing is non-fatal — quote is already saved
          console.error('Email notification failed:', errData);
        } else {
          console.log('Emails sent successfully');
        }
      } catch (emailError) {
        // Re-throw reCAPTCHA errors so the user sees them
        if (emailError.message.includes('Bot verification')) throw emailError;
        console.error('Email error (non-fatal):', emailError);
      }

      setSubmitStatus('success');
      resetForm();
      setTimeout(() => setSubmitStatus(null), 5000);

    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Get Your Estimate Today
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to transform your outdoor space? Contact us for a personalized consultation
            and estimate for your landscaping project.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                Get in Touch
              </h3>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 p-3 rounded-lg">
                    <FaPhone className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Call Us</h4>
                    <p className="text-gray-600">(603) 746-2403</p>
                    <p className="text-sm text-gray-500">Mon-Fri 8AM-5PM</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 p-3 rounded-lg">
                    <FaEnvelope className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Email Us</h4>
                    <p className="text-gray-600">info@winnepocketlandscaping.com</p>
                    <p className="text-sm text-gray-500">We'll respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 p-3 rounded-lg">
                    <FaMapMarkerAlt className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Service Area</h4>
                    <p className="text-gray-600">Central New Hampshire</p>
                    <p className="text-sm text-gray-500">Serving the Lakes Region and beyond</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 p-3 rounded-lg">
                    <FaClock className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Business Hours</h4>
                    <p className="text-gray-600">Monday - Friday: 8AM - 5PM</p>
                    <p className="text-sm text-gray-500">Saturday: By appointment</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gray-50 rounded-2xl p-8">
            {/* Success Message */}
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <FaCheckCircle className="w-5 h-5 text-green-600 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-green-800">
                      Request Submitted Successfully!
                    </h4>
                    <p className="text-sm text-green-700 mt-1">
                      Thank you for reaching out. We'll get back to you within 24 hours of the next business day.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <FaExclamationTriangle className="w-5 h-5 text-red-600 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-red-800">
                      Oops! Something went wrong.
                    </h4>
                    <p className="text-sm text-red-700 mt-1">
                      {errorMessage}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name and Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              {/* Phone and Service */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="(603) 555-0123"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                    Service Interested In
                  </label>
                  {servicesLoading ? (
                    <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 flex items-center">
                      <FaSpinner className="animate-spin w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-400 text-sm">Loading services...</span>
                    </div>
                  ) : (
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a service...</option>
                      {serviceOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* Property Address */}
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  Property Address *
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="123 Main St, Town, NH"
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Project Details *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                  placeholder="Tell us about your project, the size of your property, and any specific requirements..."
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <FaSpinner className="animate-spin w-5 h-5 mr-2" />
                    Sending...
                  </>
                ) : (
                  'Request Your Free Estimate'
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                * Required fields. We'll respond within 24 hours of the next business day.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Outer wrapper — provides reCAPTCHA context
// ─────────────────────────────────────────────────────────────────────────────
const Contact = () => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <ContactForm />
    </GoogleReCaptchaProvider>
  );
};

export default Contact;